/**
 * useAddressForm
 *
 * Manages form state, validation, and geo-data loading for AddressModal.
 */

import { useState, useEffect, useMemo } from 'react'

import { getStates, getCountryByCode, type State, type Country } from '../../../services/geolocation-service'

import type { Address } from '../../../types/billing'

export const COUNTRY_LABELS: Record<string, string> = {
  GB: 'County',
  IN: 'State',
  US: 'State',
  CA: 'Province',
  AU: 'State/Territory',
  NZ: 'Region',
  IE: 'County',
}

const PURPOSE_TYPES = new Set(['billing', 'shipping', 'consignee'])

const EMPTY_ADDRESS = (defaultType: string): Address => ({
  label: '',
  type: PURPOSE_TYPES.has(defaultType) ? 'office' : (defaultType || 'office'),
  usage: PURPOSE_TYPES.has(defaultType) ? [defaultType] : [],
  line1: '',
  line2: '',
  city: '',
  state: '',
  stateCode: '',
  postalCode: '',
  country: '',
  countryCode: '',
  gstin: '',
  companyCode: '',
  isPrimary: false,
})

export function deriveStateFieldLabel(states: State[], countryCode: string): string {
  if (countryCode && COUNTRY_LABELS[countryCode]) {
    return COUNTRY_LABELS[countryCode]
  }
  if (states.length > 0) {
    const typeCounts = new Map<string, number>()
    states.forEach(state => {
      if (state.administrativeType) {
        const count = typeCounts.get(state.administrativeType) ?? 0
        typeCounts.set(state.administrativeType, count + 1)
      }
    })
    if (typeCounts.size > 0) {
      const sorted = Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1])
      return sorted[0]?.[0] ?? 'State / Province'
    }
  }
  return 'State / Province'
}

export function validateAddress(formData: Address, states: State[]): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!formData.label?.trim()) { errors.label = 'Address label is required' }
  if (!formData.type) { errors.type = 'Address type is required' }
  if (!formData.line1?.trim()) { errors.line1 = 'Address line 1 is required' }
  if (!formData.city?.trim()) { errors.city = 'City is required' }
  if (states.length > 0 && !formData.state?.trim()) { errors.state = 'State is required' }
  if (!formData.postalCode?.trim()) { errors.postalCode = 'Postal code is required' }
  if (!formData.country?.trim()) { errors.country = 'Country is required' }
  return errors
}

export interface AddressFormState {
  formData: Address
  errors: Record<string, string>
  states: State[]
  selectedCountry: Country | null
  stateFieldLabel: string
  postalCodeLabel: string
  postalCodeExample: string
  handleChange: (field: keyof Address, value: string | boolean | string[]) => void
  handleCountryChange: (countryCode: string | null, countryName: string | null) => void
  handleStateChange: (stateCode: string | null, stateName: string | null, fullIdentifier?: string | null) => void
  clearFieldError: (field: string) => void
  handleSave: () => boolean
  handleCancel: () => void
}

interface CountryDataLoaders {
  loadStates: (countryCode: string) => Promise<void>
  loadCountryData: (countryCode: string) => Promise<void>
}

function useCountryDataLoaders(
  setStates: React.Dispatch<React.SetStateAction<State[]>>,
  setSelectedCountry: React.Dispatch<React.SetStateAction<Country | null>>,
): CountryDataLoaders {
  const loadStates = async (countryCode: string): Promise<void> => {
    try {
      const data = await getStates(countryCode)
      setStates(data)
    } catch {
      setStates([])
    }
  }
  const loadCountryData = async (countryCode: string): Promise<void> => {
    try {
      const country = await getCountryByCode(countryCode)
      setSelectedCountry(country ?? null)
    } catch {
      setSelectedCountry(null)
    }
  }
  return { loadStates, loadCountryData }
}

interface AddressFormEffectsInput {
  address: Address | null | undefined
  defaultType: string
  isOpen: boolean
  formData: Address
  loaders: CountryDataLoaders
  setFormData: React.Dispatch<React.SetStateAction<Address>>
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
  setStates: React.Dispatch<React.SetStateAction<State[]>>
  setSelectedCountry: React.Dispatch<React.SetStateAction<Country | null>>
}

function useAddressFormEffects(input: AddressFormEffectsInput): void {
  const { address, defaultType, isOpen, formData, loaders, setFormData, setErrors, setStates, setSelectedCountry } = input
  useEffect(() => {
    if (address) {
      setFormData(address)
      if (address.countryCode) {
        void loaders.loadCountryData(address.countryCode)
        void loaders.loadStates(address.countryCode)
      }
    } else {
      setFormData(EMPTY_ADDRESS(defaultType))
    }
    setErrors({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, defaultType, isOpen])

  useEffect(() => {
    if (formData.countryCode) {
      void loaders.loadStates(formData.countryCode)
      void loaders.loadCountryData(formData.countryCode)
    } else {
      setStates([])
      setSelectedCountry(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.countryCode])
}

interface AddressFormHandlersInput {
  formData: Address
  setFormData: React.Dispatch<React.SetStateAction<Address>>
  errors: Record<string, string>
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
  defaultType: string
  states: State[]
  onSave: (address: Address) => void
  onClose: () => void
}

interface AddressFormHandlers {
  handleChange: (field: keyof Address, value: string | boolean | string[]) => void
  handleCountryChange: (countryCode: string | null, countryName: string | null) => void
  handleStateChange: (stateCode: string | null, stateName: string | null, fullIdentifier?: string | null) => void
  clearFieldError: (field: string) => void
  handleSave: () => boolean
  handleCancel: () => void
}

function buildAddressFormHandlers(input: AddressFormHandlersInput): AddressFormHandlers {
  const { formData, setFormData, errors, setErrors, defaultType, states, onSave, onClose } = input
  const clearFieldError = (field: string): void => {
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }
  return {
    handleChange: (field, value) => {
      setFormData(prev => {
        const updated = { ...prev, [field]: value }
        if (field === 'countryCode' && value !== prev.countryCode) {
          updated.state = ''
          updated.stateCode = ''
        }
        return updated
      })
      clearFieldError(String(field))
    },
    handleCountryChange: (countryCode, countryName) => {
      setFormData(prev => ({ ...prev, countryCode: countryCode ?? '', country: countryName ?? '' }))
      clearFieldError('country')
    },
    handleStateChange: (stateCode, stateName) => {
      setFormData(prev => ({ ...prev, stateCode: stateCode ?? '', state: stateName ?? '' }))
      clearFieldError('state')
    },
    clearFieldError,
    handleSave: () => {
      const newErrors = validateAddress(formData, states)
      setErrors(newErrors)
      if (Object.keys(newErrors).length > 0) { return false }
      onSave(formData)
      onClose()
      return true
    },
    handleCancel: () => {
      setFormData(EMPTY_ADDRESS(defaultType))
      setErrors({})
      onClose()
    },
  }
}

export function useAddressForm(
  address: Address | null | undefined,
  defaultType: string,
  isOpen: boolean,
  onSave: (address: Address) => void,
  onClose: () => void,
): AddressFormState {
  const [formData, setFormData] = useState<Address>(EMPTY_ADDRESS(defaultType))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [states, setStates] = useState<State[]>([])
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)

  const loaders = useCountryDataLoaders(setStates, setSelectedCountry)
  useAddressFormEffects({ address, defaultType, isOpen, formData, loaders, setFormData, setErrors, setStates, setSelectedCountry })

  const stateFieldLabel = useMemo(() =>
    deriveStateFieldLabel(states, formData.countryCode ?? ''),
    [states, formData.countryCode],
  )
  const postalCodeLabel = selectedCountry?.postalCodeName ?? 'Postal Code'
  const postalCodeExample = selectedCountry?.postalCodeExample ?? '12345'
  const handlers = buildAddressFormHandlers({ formData, setFormData, errors, setErrors, defaultType, states, onSave, onClose })

  return {
    formData, errors, states, selectedCountry, stateFieldLabel,
    postalCodeLabel, postalCodeExample, ...handlers,
  }
}
